import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { apiFetch } from "../api/apiFetch";
import { formatNumber } from "../utils/format";
import ErrorModal from "../components/ErrorModal";
import { usePermission } from "../hooks/usePermission";

export default function SaleList({ user }) {
  const { hasPermission } = usePermission();
  const canCreate = hasPermission("sales.create");
  const canEdit = hasPermission("sales.edit");
  const canDelete = hasPermission("sales.delete");
  const [sales, setSales] = useState([]);
  const [brands, setBrands] = useState([]);
  const [products, setProducts] = useState([]);
  const [unauthorized, setUnauthorized] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingSales, setLoadingSales] = useState(false);
  const [updatingSaleId, setUpdatingSaleId] = useState(null);
  const [highlightedSaleId, setHighlightedSaleId] = useState(null);

  // Quick input form state
  const [selectedBrand, setSelectedBrand] = useState("");
  const [selectedProduct, setSelectedProduct] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [soldBy, setSoldBy] = useState("Katleen");
  const [defaultSoldBy, setDefaultSoldBy] = useState("Katleen");
  const [saleDate, setSaleDate] = useState(new Date().toISOString().split("T")[0]);

  // Filter and sort state
  const [filterSoldBy, setFilterSoldBy] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [filterSearch, setFilterSearch] = useState("");
  const [dateRangeShortcut, setDateRangeShortcut] = useState("");
  const [sortColumn, setSortColumn] = useState("sale_date");
  const [sortOrder, setSortOrder] = useState("desc");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;

  // Selling price for quick input
  const [sellingPrice, setSellingPrice] = useState("");

  // Edit/Delete modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingSale, setEditingSale] = useState(null);
  const [deletingSale, setDeletingSale] = useState(null);
  const [editFormData, setEditFormData] = useState({
    sale_date: "",
    sold_by: "",
    product_id: "",
    quantity: 0,
    selling_price: "",
    notes: "",
  });
  const [validationErrors, setValidationErrors] = useState({ fields: false });
  const [errorModal, setErrorModal] = useState({ show: false, message: "" });

  useEffect(() => {
    if (!user) return;
    fetchSales();
    fetchBrands();
    fetchProducts();
  }, [user, filterSoldBy, filterDateFrom, filterDateTo, filterSearch, sortColumn, sortOrder]);


  const fetchSales = () => {
    setLoadingSales(true);
    const params = new URLSearchParams({
      sort: sortColumn,
      order: sortOrder,
      limit: 1000,  // Fetch up to 1000 sales
    });

    // Combine all search filters into one search parameter
    let searchTerms = [];
    if (filterSoldBy) searchTerms.push(filterSoldBy);
    if (filterSearch) searchTerms.push(filterSearch);
    if (searchTerms.length > 0) {
      params.append("search", searchTerms.join(" "));
    }

    if (filterDateFrom) params.append("date_from", filterDateFrom);
    if (filterDateTo) params.append("date_to", filterDateTo);

    apiFetch(`/sales?${params}`)
      .then((data) => {
        // Handle new paginated response format
        if (data.items) {
          setSales(data.items);
        } else {
          // Fallback for old format (shouldn't happen)
          setSales(data);
        }
      })
      .catch((err) => {
        console.error("Fout bij ophalen verkopen:", err);
        if (err.message.includes("403")) setUnauthorized(true);
      })
      .finally(() => setLoadingSales(false));
  };

  const fetchBrands = () => {
    apiFetch("/brands?sort=name&order=asc")
      .then(setBrands)
      .catch((err) => {
        console.error("Fout bij ophalen merken:", err);
        if (err.message.includes("403")) setUnauthorized(true);
      });
  };

  const fetchProducts = () => {
    apiFetch("/products?sort=product_name&order=asc&limit=1000")
      .then((data) => {
        // Handle new paginated response format
        if (data.items) {
          setProducts(data.items);
        } else {
          setProducts(data);
        }
      })
      .catch((err) => {
        console.error("Fout bij ophalen producten:", err);
        if (err.message.includes("403")) setUnauthorized(true);
      });
  };


  const filteredProducts = selectedBrand
    ? products.filter((p) => p.brand_id === parseInt(selectedBrand))
    : products;

  // Update selling price when product changes
  const handleProductChange = (productId) => {
    setSelectedProduct(productId);
    if (productId) {
      const product = products.find(p => p.id === parseInt(productId));
      if (product && product.selling_price_incl_vat != null) {
        setSellingPrice(String(product.selling_price_incl_vat));
      } else {
        setSellingPrice("");
      }
    } else {
      setSellingPrice("");
    }
  };

  const uniqueSoldBy = [...new Set(sales.map(sale => sale.sold_by))].sort();

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortOrder("asc");
    }
  };

  const handleDateRangeShortcut = (shortcut) => {
    setDateRangeShortcut(shortcut);

    const today = new Date();
    const formatDate = (date) => date.toISOString().split("T")[0];

    switch (shortcut) {
      case "today":
        setFilterDateFrom(formatDate(today));
        setFilterDateTo(formatDate(today));
        break;

      case "current_week": {
        const dayOfWeek = today.getDay();
        const monday = new Date(today);
        monday.setDate(today.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);
        setFilterDateFrom(formatDate(monday));
        setFilterDateTo(formatDate(sunday));
        break;
      }

      case "previous_week": {
        const dayOfWeek = today.getDay();
        const previousMonday = new Date(today);
        previousMonday.setDate(today.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1) - 7);
        const previousSunday = new Date(previousMonday);
        previousSunday.setDate(previousMonday.getDate() + 6);
        setFilterDateFrom(formatDate(previousMonday));
        setFilterDateTo(formatDate(previousSunday));
        break;
      }

      case "current_month": {
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
        const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        setFilterDateFrom(formatDate(firstDay));
        setFilterDateTo(formatDate(lastDay));
        break;
      }

      case "previous_month": {
        const firstDay = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastDay = new Date(today.getFullYear(), today.getMonth(), 0);
        setFilterDateFrom(formatDate(firstDay));
        setFilterDateTo(formatDate(lastDay));
        break;
      }

      case "current_year": {
        const firstDay = new Date(today.getFullYear(), 0, 1);
        const lastDay = new Date(today.getFullYear(), 11, 31);
        setFilterDateFrom(formatDate(firstDay));
        setFilterDateTo(formatDate(lastDay));
        break;
      }

      default:
        setFilterDateFrom("");
        setFilterDateTo("");
    }
  };

  const clearFilters = () => {
    setFilterSoldBy("");
    setFilterDateFrom("");
    setFilterDateTo("");
    setFilterSearch("");
    setDateRangeShortcut("");
    setCurrentPage(1);
  };

  const hasActiveFilters = filterSoldBy || filterDateFrom || filterDateTo || filterSearch;

  // Calculate pagination
  const totalPages = Math.ceil(sales.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedSales = sales.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterSoldBy, filterDateFrom, filterDateTo, filterSearch, sortColumn, sortOrder]);

  const handleAddSale = () => {
    if (!selectedProduct || !quantity || !saleDate) {
      setValidationErrors({ fields: true });
      return;
    }
    setValidationErrors({ fields: false });

    setLoading(true);

    const payload = {
      sale_date: saleDate,
      sold_by: soldBy,
      product_id: parseInt(selectedProduct),
      quantity: parseFloat(quantity),
      selling_price: sellingPrice ? parseFloat(sellingPrice) : null,
      notes: null,
    };

    apiFetch("/sales", {
      method: "POST",
      body: JSON.stringify(payload),
    })
      .then((updatedSale) => {
        // Update the specific sale in the list or add new one
        setSales((prevSales) => {
          const existingIndex = prevSales.findIndex(s => s.id === updatedSale.id);
          if (existingIndex !== -1) {
            // Update existing sale
            const newSales = [...prevSales];
            newSales[existingIndex] = updatedSale;
            return newSales;
          } else {
            // Add new sale
            return [updatedSale, ...prevSales];
          }
        });

        // Highlight the updated sale
        setHighlightedSaleId(updatedSale.id);
        setTimeout(() => setHighlightedSaleId(null), 2000);

        // Reset form
        setSelectedProduct("");
        setQuantity(1);
        setSellingPrice("");
      })
      .catch((err) => {
        console.error("Fout bij toevoegen verkoop:", err);
        setErrorModal({ show: true, message: "Er is een fout opgetreden bij het toevoegen van de verkoop." });
        if (err.message.includes("403")) setUnauthorized(true);
      })
      .finally(() => setLoading(false));
  };

  const handleClear = () => {
    setSelectedBrand("");
    setSelectedProduct("");
    setQuantity(1);
    setSellingPrice("");
    setDefaultSoldBy("Katleen");
    setSoldBy("Katleen");
  };

  const handleIncrementQuantity = () => {
    setQuantity((prev) => prev + 1);
  };

  const handleDecrementQuantity = () => {
    setQuantity((prev) => Math.max(1, prev - 1));
  };

  const handleQuickIncrement = (saleId) => {
    setUpdatingSaleId(saleId);
    apiFetch(`/sales/${saleId}/increment`, {
      method: "PATCH",
    })
      .then((updatedSale) => {
        // Update only the specific sale in the list
        setSales((prevSales) =>
          prevSales.map((sale) =>
            sale.id === updatedSale.id ? updatedSale : sale
          )
        );

        // Highlight the updated sale
        setHighlightedSaleId(updatedSale.id);
        setTimeout(() => setHighlightedSaleId(null), 2000);
      })
      .catch((err) => {
        console.error("Fout bij verhogen aantal:", err);
        setErrorModal({ show: true, message: "Er is een fout opgetreden bij het verhogen van het aantal." });
      })
      .finally(() => setUpdatingSaleId(null));
  };

  const handleEdit = (sale) => {
    setEditingSale(sale);
    setEditFormData({
      sale_date: sale.sale_date,
      sold_by: sale.sold_by,
      product_id: sale.product_id,
      quantity: sale.quantity,
      selling_price: sale.selling_price != null ? String(sale.selling_price) : "",
      notes: sale.notes || "",
    });
    setShowEditModal(true);
  };

  const handleDelete = (sale) => {
    setDeletingSale(sale);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (!deletingSale) return;

    setLoading(true);
    apiFetch(`/sales/${deletingSale.id}`, { method: "DELETE" })
      .then(() => {
        setShowDeleteModal(false);
        setDeletingSale(null);
        fetchSales();
      })
      .catch((err) => {
        console.error("Fout bij verwijderen verkoop:", err);
        setErrorModal({ show: true, message: "Er is een fout opgetreden bij het verwijderen van de verkoop." });
      })
      .finally(() => setLoading(false));
  };

  const handleEditSubmit = (e) => {
    e.preventDefault();
    if (!editingSale) return;

    setLoading(true);

    const payload = {
      sale_date: editFormData.sale_date,
      sold_by: editFormData.sold_by,
      product_id: parseInt(editFormData.product_id),
      quantity: parseFloat(editFormData.quantity),
      selling_price: editFormData.selling_price ? parseFloat(editFormData.selling_price) : null,
      notes: editFormData.notes || null,
    };

    apiFetch(`/sales/${editingSale.id}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    })
      .then(() => {
        setShowEditModal(false);
        setEditingSale(null);
        fetchSales();
      })
      .catch((err) => {
        console.error("Fout bij bijwerken verkoop:", err);
        setErrorModal({ show: true, message: "Er is een fout opgetreden bij het bijwerken van de verkoop." });
      })
      .finally(() => setLoading(false));
  };

  const isToday = (dateString) => {
    const today = new Date().toISOString().split("T")[0];
    return dateString === today;
  };

  if (unauthorized) return <Navigate to="/unauthorized" replace />;
  if (!user) return <Navigate to="/login" replace />;

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Verkopen</h1>
      </div>

      {/* Quick Input Form - only show if user can create sales */}
      {canCreate && (
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <div className="flex gap-2 items-end">
          <div style={{ width: '180px' }}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Verkocht door
            </label>
            <input
              type="text"
              value={soldBy}
              onChange={(e) => {
                setSoldBy(e.target.value);
                setDefaultSoldBy(e.target.value);
              }}
              className="w-full px-3 border rounded"
              style={{ height: '42px' }}
              placeholder="Naam"
            />
          </div>

          <div style={{ width: '135px' }}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Datum
            </label>
            <input
              type="date"
              value={saleDate}
              onChange={(e) => setSaleDate(e.target.value)}
              className="w-full px-3 border rounded"
              style={{ height: '42px' }}
            />
          </div>

          <div style={{ width: '140px' }}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Merk
            </label>
            <select
              value={selectedBrand}
              onChange={(e) => {
                setSelectedBrand(e.target.value);
                setSelectedProduct("");
              }}
              className="w-full px-3 border rounded"
              style={{ height: '42px' }}
            >
              <option value="">-- Alle --</option>
              {brands.map((brand) => (
                <option key={brand.id} value={brand.id}>
                  {brand.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex-1" style={{ minWidth: '250px' }}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Product
            </label>
            <select
              value={selectedProduct}
              onChange={(e) => handleProductChange(e.target.value)}
              className="w-full px-3 border rounded"
              style={{ height: '42px' }}
            >
              <option value="">-- Selecteer product --</option>
              {filteredProducts.map((product) => (
                <option key={product.id} value={product.id}>
                  {product.product_name} {product.sku ? `(${product.sku})` : ""}
                </option>
              ))}
            </select>
          </div>

          <div style={{ width: '100px' }}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Verkoopprijs
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">€</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={sellingPrice}
                onChange={(e) => setSellingPrice(e.target.value)}
                className="w-full pl-7 pr-2 border rounded"
                style={{ height: '42px' }}
                placeholder="0.00"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Aantal
            </label>
            <div className="flex" style={{ height: '42px' }}>
              <button
                type="button"
                onClick={handleDecrementQuantity}
                className="px-3 border border-r-0 rounded-l bg-gray-100 hover:bg-gray-200"
              >
                -
              </button>
              <input
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                className="px-3 border text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                style={{ width: '50px' }}
              />
              <button
                type="button"
                onClick={handleIncrementQuantity}
                className="px-3 border border-l-0 rounded-r bg-gray-100 hover:bg-gray-200"
              >
                +
              </button>
            </div>
          </div>

          <button
            onClick={handleAddSale}
            disabled={loading}
            className="px-6 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
            style={{ height: '42px' }}
          >
            Toevoegen
          </button>
          <button
            onClick={handleClear}
            disabled={loading}
            className="px-4 border rounded hover:bg-gray-100"
            style={{ height: '42px' }}
          >
            Wissen
          </button>
        </div>
        {validationErrors.fields && (
          <p className="mt-2 text-sm text-red-600">Vul alle velden in om een verkoop toe te voegen.</p>
        )}
      </div>
      )}

      {/* Filters */}
      <div className="mb-4 flex flex-wrap gap-2 items-end">
        <input
          type="text"
          placeholder="Zoek op merk of product..."
          value={filterSearch}
          onChange={(e) => setFilterSearch(e.target.value)}
          className="w-full max-w-md px-4 py-2 border rounded"
        />

        <select
          value={filterSoldBy}
          onChange={(e) => setFilterSoldBy(e.target.value)}
          className="px-4 py-2 border rounded"
        >
          <option value="">Alle verkopers</option>
          {uniqueSoldBy.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>

        <select
          value={dateRangeShortcut}
          onChange={(e) => handleDateRangeShortcut(e.target.value)}
          className="px-4 py-2 border rounded"
        >
          <option value="">Alle datums</option>
          <option value="today">Vandaag</option>
          <option value="current_week">Huidige week</option>
          <option value="previous_week">Vorige week</option>
          <option value="current_month">Huidige maand</option>
          <option value="previous_month">Vorige maand</option>
          <option value="current_year">Huidig jaar</option>
          <option value="custom">Custom...</option>
        </select>

        {dateRangeShortcut === "custom" && (
          <>
            <input
              type="date"
              value={filterDateFrom}
              onChange={(e) => setFilterDateFrom(e.target.value)}
              placeholder="Van"
              className="px-4 py-2 border rounded"
            />
            <input
              type="date"
              value={filterDateTo}
              onChange={(e) => setFilterDateTo(e.target.value)}
              placeholder="Tot"
              className="px-4 py-2 border rounded"
            />
          </>
        )}

        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="px-4 py-2 border rounded hover:bg-gray-100 text-gray-700"
          >
            Wissen
          </button>
        )}
      </div>

      {/* Sales Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {loadingSales && (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">Verkopen laden...</span>
          </div>
        )}

        {!loadingSales && sales.length === 0 && (
          <div className="p-8 text-center text-gray-500">
            Nog geen verkopen geregistreerd.
          </div>
        )}

        {!loadingSales && sales.length > 0 && (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort("sale_date")}
                >
                  <div className="flex items-center gap-1">
                    Datum
                    {sortColumn === "sale_date" && (
                      <span>{sortOrder === "asc" ? "↑" : "↓"}</span>
                    )}
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort("sold_by")}
                >
                  <div className="flex items-center gap-1">
                    Verkocht door
                    {sortColumn === "sold_by" && (
                      <span>{sortOrder === "asc" ? "↑" : "↓"}</span>
                    )}
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort("brand")}
                >
                  <div className="flex items-center gap-1">
                    Merk
                    {sortColumn === "brand" && (
                      <span>{sortOrder === "asc" ? "↑" : "↓"}</span>
                    )}
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort("product")}
                >
                  <div className="flex items-center gap-1">
                    Product
                    {sortColumn === "product" && (
                      <span>{sortOrder === "asc" ? "↑" : "↓"}</span>
                    )}
                  </div>
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase">
                  Aantal
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase">
                  Verkoopprijs
                </th>
                {(canEdit || canDelete) && (
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase">
                    Acties
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedSales.map((sale) => (
                <tr
                  key={sale.id}
                  className={`hover:bg-gray-50 transition-colors duration-500 ${
                    highlightedSaleId === sale.id ? 'bg-green-100' : ''
                  }`}
                >
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {new Date(sale.sale_date).toLocaleDateString("nl-BE")}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">{sale.sold_by}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{sale.brand_name || "-"}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{sale.product_name || "-"}</td>
                  <td className={`px-4 py-3 text-sm text-right text-gray-900 transition-all duration-500 ${
                    updatingSaleId === sale.id ? 'opacity-50' : ''
                  }`}>
                    {updatingSaleId === sale.id ? (
                      <div className="inline-flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                        {formatNumber(sale.quantity)}
                      </div>
                    ) : (
                      formatNumber(sale.quantity)
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-right text-gray-900">
                    {sale.selling_price != null ? `€${formatNumber(sale.selling_price)}` : "-"}
                  </td>
                  {(canEdit || canDelete) && (
                    <td className="px-4 py-3 text-right">
                      {canEdit && isToday(sale.sale_date) && (
                        <button
                          onClick={() => handleQuickIncrement(sale.id)}
                          disabled={updatingSaleId === sale.id}
                          className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 disabled:bg-gray-400 mr-2"
                        >
                          +1
                        </button>
                      )}
                      {canEdit && (
                        <button
                          onClick={() => handleEdit(sale)}
                          className="text-blue-600 hover:text-blue-900 text-sm mr-3"
                        >
                          Bewerken
                        </button>
                      )}
                      {canDelete && (
                        <button
                          onClick={() => handleDelete(sale)}
                          className="text-red-600 hover:text-red-900 text-sm"
                        >
                          Verwijderen
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {!loadingSales && sales.length > itemsPerPage && (
        <div className="mt-4 flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Toon {startIndex + 1} tot {Math.min(endIndex, sales.length)} van {sales.length} verkopen
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="px-3 py-2 border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Vorige
            </button>

            <div className="flex items-center gap-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                // Show first page, last page, current page, and pages around current
                const showPage =
                  page === 1 ||
                  page === totalPages ||
                  (page >= currentPage - 1 && page <= currentPage + 1);

                const showEllipsis =
                  (page === currentPage - 2 && currentPage > 3) ||
                  (page === currentPage + 2 && currentPage < totalPages - 2);

                if (showEllipsis) {
                  return <span key={page} className="px-2 text-gray-500">...</span>;
                }

                if (!showPage) {
                  return null;
                }

                return (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`px-3 py-2 border rounded ${
                      currentPage === page
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'hover:bg-gray-100'
                    }`}
                  >
                    {page}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-2 border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Volgende
            </button>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingSale && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Verkoop Bewerken</h2>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleEditSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Datum *
                  </label>
                  <input
                    type="date"
                    required
                    value={editFormData.sale_date}
                    onChange={(e) =>
                      setEditFormData({ ...editFormData, sale_date: e.target.value })
                    }
                    className="w-full px-3 py-2 border rounded"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Verkocht door *
                  </label>
                  <input
                    type="text"
                    required
                    value={editFormData.sold_by}
                    onChange={(e) =>
                      setEditFormData({ ...editFormData, sold_by: e.target.value })
                    }
                    className="w-full px-3 py-2 border rounded"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Product *
                  </label>
                  <select
                    required
                    value={editFormData.product_id}
                    onChange={(e) =>
                      setEditFormData({ ...editFormData, product_id: e.target.value })
                    }
                    className="w-full px-3 py-2 border rounded"
                  >
                    <option value="">-- Selecteer product --</option>
                    {products.map((product) => (
                      <option key={product.id} value={product.id}>
                        {product.product_name} {product.sku ? `(${product.sku})` : ""}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Aantal *
                  </label>
                  <div className="flex">
                    <button
                      type="button"
                      onClick={() =>
                        setEditFormData({ ...editFormData, quantity: Math.max(0.01, editFormData.quantity - 1) })
                      }
                      className="px-3 py-2 border border-r-0 rounded-l bg-gray-100 hover:bg-gray-200"
                    >
                      -
                    </button>
                    <input
                      type="number"
                      step="0.01"
                      required
                      value={editFormData.quantity}
                      onChange={(e) =>
                        setEditFormData({ ...editFormData, quantity: parseFloat(e.target.value) || 0 })
                      }
                      className="w-full px-3 py-2 border text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setEditFormData({ ...editFormData, quantity: editFormData.quantity + 1 })
                      }
                      className="px-3 py-2 border border-l-0 rounded-r bg-gray-100 hover:bg-gray-200"
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Verkoopprijs (€)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={editFormData.selling_price}
                    onChange={(e) =>
                      setEditFormData({ ...editFormData, selling_price: e.target.value })
                    }
                    className="w-full px-3 py-2 border rounded"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Opmerkingen
                  </label>
                  <textarea
                    value={editFormData.notes}
                    onChange={(e) =>
                      setEditFormData({ ...editFormData, notes: e.target.value })
                    }
                    className="w-full px-3 py-2 border rounded"
                    rows="2"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 border rounded hover:bg-gray-100"
                  disabled={loading}
                >
                  Annuleren
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
                  disabled={loading}
                >
                  {loading ? "Bezig..." : "Opslaan"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deletingSale && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Verkoop Verwijderen</h2>
            <p className="mb-6 text-gray-700">
              Weet je zeker dat je de verkoop van{" "}
              <strong>{new Date(deletingSale.sale_date).toLocaleDateString("nl-BE")}</strong>{" "}
              door <strong>{deletingSale.sold_by}</strong> wilt verwijderen?
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeletingSale(null);
                }}
                className="px-4 py-2 border rounded hover:bg-gray-100"
                disabled={loading}
              >
                Annuleren
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:bg-gray-400"
                disabled={loading}
              >
                {loading ? "Bezig..." : "Verwijderen"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      <ErrorModal
        show={errorModal.show}
        message={errorModal.message}
        onClose={() => setErrorModal({ show: false, message: "" })}
      />
    </div>
  );
}
